import PayloadHelper from './PayloadHelper';
import Game from '../../Game';
import { PayloadType } from './PayloadType';
import { Random } from '../Random';
import { GameActions } from '../../State/Game/GameActions';
import { Leaderboard } from '../../System/UI/LeaderboardHelper';
import { LevelActions } from '../../State/Level/LevelActions';
import { IAdvertisementPayload, IAdvertisementPlacement } from './Advertisement';
import { getCurrentAnimatorAndScene, StartAnimation } from '../../System/Animation/AnimationHelper';
import { currencySendReward, FRIEND_GIFT_REWARD } from '../../Constants/Economy';
import { HelperFriendActions } from '../../State/Level/HelperFriend/HelperFriendActions';
import { isIOS } from '../../Utility/Platform';
import { IAPReward } from '../../Utility/RewardHelper';
import { IRewardData } from '../RewardHelper';
import { FBTrackingEvent } from '../Tracking/FBTrackingEvent';
import { track } from '../Tracking/Track';
import { trackResourceEvent } from '../Tracking/TrackingHelpers/Transaction';
import { TActionType, TGameArea, TransactionType, TSocialActionType } from '../Tracking/DataSet/Transaction';
import { addAndSendSocialActionEvent, SocialActionTypes } from '../Tracking/DataSet/SocialAction';
import { trackReward } from '../Tracking/TrackingHelpers/Resource';
import { IGameState } from '../../State/Game/IGameState';
import { ResponseStatus, ResponseType } from '../../State/Game/ContextType';
import { IScoreEntry } from './Drivers/IScoreEntry';
import { IMessagePayload } from '../../State/Game/IEntryPoint';
import { PetId } from '../../Constants/Pets';
import { ILuckyWheelShareData } from 'System/UI/WheelPopup/WheelPopup';
import { purchaseProduct } from '@platform/Utility/SDK/IAP/PurchaseProduct';

export interface IShareData {
    message?: string;
    CTA?: string;
    base64?: string;
    payload?: IMessagePayload;
}

export default class SDKIntegrationsHelper {
    public static async sendCurrency(friendId: string) {
        const base64Image = await PayloadHelper.getSendCoinsBase64();

        try {
            await SDKIntegrationsHelper.connectContext(friendId);

            Game.sdk.customUpdate(
                Game.locale.getText('DontWorryIHelped'),
                base64Image,
                friendId,
                {
                    data: PayloadType.SEND_CURRENCY,
                    messageId: Random.ID(),
                    userId: Game.sdk.getUserId(),
                    userName: Game.sdk.getUsername(),
                    userAvatar: Game.sdk.getAvatar(),
                    reward: currencySendReward,
                    customEntryPoint: SocialActionTypes.CUSTOM_SEND_CURRENCY,
                },
                'IMMEDIATE',
                Game.locale.getText('LetsPlay')
            );

            GameActions.restoreContext(ResponseStatus.SUCCESS, ResponseType.SEND_CURRENCY, friendId);

            addAndSendSocialActionEvent({
                type: SocialActionTypes.CUSTOM_SEND_CURRENCY,
                text: 'DontWorryIHelped',
                friendId,
            });
        } catch (e) {
            console.info(e);
            GameActions.restoreContext(ResponseStatus.FAIL, ResponseType.SEND_CURRENCY, e);
        }
    }

    public static async requestCurrency(friendId: string) {
        const base64Image = await PayloadHelper.getRequestCoinsBase64();
        let status: ResponseStatus = ResponseStatus.FAIL;

        await SDKIntegrationsHelper.connectContext(friendId).catch(e =>
            SDKIntegrationsHelper.restoreContext(ResponseStatus.FAIL, ResponseType.REQUEST_CURRENCY, e)
        );

        Game.sdk
            .customUpdate(
                Game.locale.getText('RequestCurrencyTitle'),
                base64Image,
                friendId,
                {
                    data: PayloadType.REQUEST_CURRENCY,
                    messageId: Random.ID(),
                    userId: Game.sdk.getUserId(),
                    userName: Game.sdk.getUsername(),
                    userAvatar: Game.sdk.getAvatar(),
                    customEntryPoint: SocialActionTypes.CUSTOM_REQUEST_CURRENCY,
                },
                'IMMEDIATE',
                Game.locale.getText('HelpYourFriend')
            )
            .then(() => {
                SDKIntegrationsHelper.restoreContext(ResponseStatus.SUCCESS, ResponseType.REQUEST_CURRENCY, friendId);
                Game.sdk.gameTracking(FBTrackingEvent.Coin_Request);

                addAndSendSocialActionEvent({
                    type: SocialActionTypes.CUSTOM_REQUEST_CURRENCY,
                    text: 'RequestCurrencyTitle',
                    friendId,
                });
            })
            .catch(e => SDKIntegrationsHelper.restoreContext(ResponseStatus.FAIL, ResponseType.REQUEST_CURRENCY, e));
    }

    public static async giveCurrency(data: { payload: IMessagePayload; reward: IRewardData }) {
        const base64Image = await PayloadHelper.getSendCoinsBase64();
        const message: string = Game.locale.getText('GotCoinsFrom').replace('[x]', Game.sdk.getUsername());

        try {
            if (Game.sdk.getContext() === null) {
                await SDKIntegrationsHelper.connectContext(data.payload.userId);
            }

            Game.sdk.customUpdate(
                message,
                base64Image,
                data.payload.userId,
                {
                    data: PayloadType.SEND_CURRENCY,
                    messageId: Random.ID(),
                    userId: Game.sdk.getUserId(),
                    userAvatar: Game.sdk.getAvatar(),
                    userName: Game.sdk.getUsername(),
                    reward: data.reward,
                    customEntryPoint: SocialActionTypes.CUSTOM_GIVE_CURRENCY,
                },
                'IMMEDIATE',
                Game.locale.getText('Collect')
            );

            GameActions.restoreContext(ResponseStatus.SUCCESS, ResponseType.GIVE_CURRENCY, data);
            Game.sdk.gameTracking(FBTrackingEvent.Coin_Send);

            addAndSendSocialActionEvent({
                type: SocialActionTypes.CUSTOM_GIVE_CURRENCY,
                text: 'GotCoinsFrom',
                friendId: data.payload.userId,
            });
        } catch (e) {
            console.log(e);
            GameActions.restoreContext(ResponseStatus.FAIL, ResponseType.GIVE_CURRENCY, e);
        }
    }

    public static async inviteFriend(placement: string) {
        const base64Image = await PayloadHelper.getInviteFriendBase64();
        try {
            const playerId: string = await Game.sdk.chooseFriend();

            Game.sdk.customUpdate(
                Game.locale.getText('InviteMessage'),
                base64Image,
                playerId,
                {
                    data: PayloadType.INVITE_FRIEND,
                    placement,
                    messageId: Random.ID(),
                    userId: Game.sdk.getUserId(),
                    userName: Game.sdk.getUsername(),
                    userAvatar: Game.sdk.getAvatar(),
                    customEntryPoint: SocialActionTypes.CUSTOM_INVITE_FRIEND,
                },
                'IMMEDIATE',
                Game.locale.getText('InviteCTA')
            );

            GameActions.restoreContext(ResponseStatus.SUCCESS, ResponseType.INVITE_FRIEND);

            if (placement === 'SocialHub') {
                Game.sdk.gameTracking(FBTrackingEvent.SocialHub_Invite);
            } else if (placement === 'TopList') {
                Game.sdk.gameTracking(FBTrackingEvent.TopList_Invite);
            }

            addAndSendSocialActionEvent({
                type: SocialActionTypes.CUSTOM_INVITE_FRIEND,
                text: 'InviteMessage',
                friendId: playerId,
            });
        } catch (e) {
            GameActions.restoreContext(ResponseStatus.FAIL, ResponseType.INVITE_FRIEND, e);
        }
    }

    public static async sendGift(
        state: IGameState,
        data: {
            friendId: string;
            altGift: boolean;
            placement: string;
        }
    ) {
        const base64Image = await PayloadHelper.getFriendGiftBase64();
        try {
            if (!isIOS()) {
                await SDKIntegrationsHelper.connectContext(data.friendId);

                if (data.placement === 'SagaMap') {
                    Game.sdk.gameTracking(FBTrackingEvent.SagaMap_Coins_Show);
                } else if (data.placement === 'TopList') {
                    Game.sdk.gameTracking(FBTrackingEvent.TopList_Coins_Show);
                }

                Game.sdk
                    .customUpdate(
                        Game.locale.getText('FriendGotCoins'),
                        base64Image,
                        data.friendId,
                        {
                            data: PayloadType.SEND_FRIEND_GIFT,
                            placement: data.placement,
                            messageId: Random.ID(),
                            userId: Game.sdk.getUserId(),
                            userName: Game.sdk.getUsername(),
                            userAvatar: Game.sdk.getAvatar(),
                            customEntryPoint: SocialActionTypes.CUSTOM_SEND_GIFT,
                        },
                        'IMMEDIATE',
                        Game.locale.getText('InviteCTA')
                    )
                    .then(() => {
                        addAndSendSocialActionEvent({
                            type: SocialActionTypes.CUSTOM_SEND_GIFT,
                            text: 'FriendGotCoins',
                            friendId: data.friendId,
                        });
                    })
                    .catch(e => console.warn(e));
            }

            const { animator, scene } = getCurrentAnimatorAndScene(state.activeScene.result);
            StartAnimation(animator, 'RewardCoins', {
                scene,
                value: FRIEND_GIFT_REWARD.value,
                previousValue: state.playerState.inventory.coins,
            });

            GameActions.restoreContext(ResponseStatus.SUCCESS, ResponseType.SEND_FRIEND_GIFT, data);

            if (data.placement === 'SagaMap') {
                Game.sdk.gameTracking(FBTrackingEvent.SagaMap_Coins_Claim);
                track('tGameArea', TGameArea.MAP);
            } else if (data.placement === 'TopList') {
                Game.sdk.gameTracking(FBTrackingEvent.TopList_Coins_Claim);
                track('tGameArea', TGameArea.LEVEL);
            }
            trackReward(FRIEND_GIFT_REWARD);
            track('socialSend', 1);
            track('socialActionType', TSocialActionType.GET_COINS);

            trackResourceEvent(Game.store.getState(), {
                transactionType: TransactionType.REWARD,
                tActionType: TActionType.SOCIAL,
            });
        } catch (e) {
            console.info(e);
            GameActions.restoreContext(ResponseStatus.FAIL, ResponseType.SEND_FRIEND_GIFT, e);
        }
    }

    public static async helperFriendRequest(friendId: string) {
        const base64Image = await PayloadHelper.getHelpFriendBase64();
        try {
            // Attempt to switch into context.
            await SDKIntegrationsHelper.connectContext(friendId);

            Game.sdk.gameTracking(FBTrackingEvent.Helper_Confirm);

            const messageKey = isIOS() ? 'HelpFriendMessage' : 'HelpFriendMessageExtraCard';
            const ctaKey = isIOS() ? 'HelpFriendCTA' : 'LetsPlay';
            Game.sdk.customUpdate(
                Game.locale.getText(messageKey),
                base64Image,
                friendId,
                {
                    data: PayloadType.HELP_FRIEND,
                    messageId: Random.ID(),
                    userId: Game.sdk.getUserId(),
                    userName: Game.sdk.getUsername(),
                    userAvatar: Game.sdk.getAvatar(),
                    cards: 1,
                    customEntryPoint: SocialActionTypes.CUSTOM_FRIEND_REQUEST,
                },
                'IMMEDIATE',
                Game.locale.getText(ctaKey)
            );
            HelperFriendActions.helperFriendResponse(true, friendId);

            addAndSendSocialActionEvent({
                type: SocialActionTypes.CUSTOM_FRIEND_REQUEST,
                text: messageKey,
                friendId,
            });
        } catch (e) {
            HelperFriendActions.helperFriendResponse(false);
        }
    }

    public static async handlePurchaseProduct(
        state: IGameState,
        data: {
            product: IAPReward;
        }
    ) {
        await purchaseProduct(state, data);
    }


    public static async shareBeatHighscoreLevel(
        levelNumber: number,
        playerEntry: IScoreEntry,
        friendEntry: IScoreEntry
    ) {
        const base64Image = await PayloadHelper.getBeatHighscoreLevelBase64(levelNumber, playerEntry, friendEntry);

        try {
            try {
                await Game.sdk.createContext(friendEntry.user.playerId);
            } catch (e) {
                // Ignore "SAME_CONTEXT" error.
                console.info(e);
                if (e.code !== 'SAME_CONTEXT') {
                    throw e;
                }
            }

            await Game.sdk.customUpdate(Game.locale.getText('BeatMyScore'), base64Image, friendEntry.user.playerId, {
                data: PayloadType.SHARE_BEAT_HIGHSCORE_LEVEL,
                messageId: Random.ID(),
                userId: Game.sdk.getUserId(),
                userName: Game.sdk.getUsername(),
                userAvatar: Game.sdk.getAvatar(),
                customEntryPoint: SocialActionTypes.CUSTOM_BEST_HIGH_SCORE_LEVEL,
            });

            GameActions.restoreContext(ResponseStatus.SUCCESS, ResponseType.SHARE_BEAT_HIGHSCORE_LEVEL);

            addAndSendSocialActionEvent({
                type: SocialActionTypes.CUSTOM_BEST_HIGH_SCORE_LEVEL,
                text: 'BeatMyScore',
                friendId: friendEntry.user.playerId,
            });

            // SHARE HIGHT SCORE LEVEL
        } catch (e) {
            GameActions.restoreContext(ResponseStatus.FAIL, ResponseType.SHARE_BEAT_HIGHSCORE_LEVEL, e);
        }
    }

    public static async share3StarReward(data: { levelNumber: number; worldNumber: number; firstComplete: boolean }) {
        const base64Image = await PayloadHelper.get3StarShareBase64(data.levelNumber);
        try {
            await Game.sdk.share(
                Game.locale.getText('Share3StarReward'),
                base64Image,
                {
                    data: PayloadType.SHARE_3_STAR_REWARD,
                    customEntryPoint: SocialActionTypes.WALLPOST_SHARE_3_STAR_REWARD,
                },
                'REQUEST'
            );

            GameActions.restoreContext(ResponseStatus.SUCCESS, ResponseType.SHARE_3_STAR_REWARD, data);
            Game.sdk.gameTracking(FBTrackingEvent.Three_Stars_Share);

            addAndSendSocialActionEvent({
                type: SocialActionTypes.WALLPOST_SHARE_3_STAR_REWARD,
                text: 'Share3StarReward',
            });
        } catch (e) {
            GameActions.restoreContext(ResponseStatus.FAIL, ResponseType.SHARE_3_STAR_REWARD, data);
        }
    }

    public static async shareHardLevel(data: { levelNumber: number; worldNumber: number; firstComplete: boolean }) {
        const base64Image = await PayloadHelper.getHardLevelShareBase64();
        try {
            await Game.sdk.share(
                Game.locale.getText('ShareHardLevel'),
                base64Image,
                {
                    data: PayloadType.SHARE_HARD_LEVEL,
                    customEntryPoint: SocialActionTypes.WALLPOST_SHARE_HARD_LEVEL,
                },
                'REQUEST'
            );

            GameActions.restoreContext(ResponseStatus.SUCCESS, ResponseType.SHARE_HARD_LEVEL, data);
            Game.sdk.gameTracking(FBTrackingEvent.Hard_Level_Share);

            addAndSendSocialActionEvent({
                type: SocialActionTypes.WALLPOST_SHARE_HARD_LEVEL,
                text: 'ShareHardLevel',
            });
        } catch (e) {
            GameActions.restoreContext(ResponseStatus.FAIL, ResponseType.SHARE_HARD_LEVEL, data);
        }
    }

    public static async shareFarm() {
        const base64Image = PayloadHelper.getFarmShareBase64();
        try {
            await Game.sdk.share(
                'FarmShareGarden',
                base64Image,
                {
                    data: PayloadType.SHARE_FARM,
                    customEntryPoint: SocialActionTypes.WALLPOST_SHARE_FARM,
                },
                'REQUEST'
            );

            GameActions.restoreContext(ResponseStatus.SUCCESS, ResponseType.SHARE_FARM, {});
            Game.sdk.gameTracking(FBTrackingEvent.Farm_Share);

            addAndSendSocialActionEvent({
                type: SocialActionTypes.WALLPOST_SHARE_FARM,
                text: 'FarmShareGarden',
            });
        } catch (e) {
            GameActions.restoreContext(ResponseStatus.FAIL, ResponseType.SHARE_FARM, {});
        }
    }

    public static async shareScreenshot(message: string, payloadData: PayloadType) {
        try {
            await Game.sdk.share(message, PayloadHelper.getScreenshotBase64(), { data: payloadData }, 'SHARE');
            GameActions.restoreContext(ResponseStatus.SUCCESS, ResponseType.SHARE_SCREENSHOT);
        } catch (e) {
            GameActions.restoreContext(ResponseStatus.FAIL, ResponseType.SHARE_SCREENSHOT, e);
        }
    }

    public static async shareCropUnlock(fieldId: string) {
        const base64Image = await PayloadHelper.getCropUnlockBase64(fieldId);

        try {
            await Game.sdk.share(
                Game.locale.getText('ShareFieldMessage'),
                base64Image,
                {
                    data: PayloadType.SHARE_CROP_UNLOCKED,
                    customEntryPoint: SocialActionTypes.WALLPOST_SHARE_CROP_UNLOCKED,
                },
                'REQUEST'
            );
            GameActions.restoreContext(ResponseStatus.SUCCESS, ResponseType.SHARE_CROP_UNLOCKED);
            Game.sdk.gameTracking(FBTrackingEvent.NewField_Share);

            addAndSendSocialActionEvent({
                type: SocialActionTypes.WALLPOST_SHARE_CROP_UNLOCKED,
                text: 'ShareFieldMessage',
            });
        } catch (e) {
            GameActions.restoreContext(ResponseStatus.FAIL, ResponseType.SHARE_CROP_UNLOCKED, e);
        }
    }

    public static async sharePetUnlock(petId: PetId) {
        const base64Image = await PayloadHelper.getPetUnlockBase64(petId);

        try {
            await Game.sdk.share(
                Game.locale.getText(`${petId}SharePetUnlock`),
                base64Image,
                {
                    data: PayloadType.SHARE_PET_UNLOCKED,
                    customEntryPoint: SocialActionTypes.WALLPOST_SHARE_CROP_UNLOCKED,
                },
                'REQUEST'
            );
            GameActions.restoreContext(ResponseStatus.SUCCESS, ResponseType.SHARE_PET_UNLOCKED);

            Game.sdk.gameTracking(FBTrackingEvent.Pet_Unlock_Share);
            addAndSendSocialActionEvent({
                type: SocialActionTypes.WALLPOST_SHARE_PET_UNLOCKED,
                text: 'SharePetUnlocked',
            });
        } catch (e) {
            GameActions.restoreContext(ResponseStatus.FAIL, ResponseType.SHARE_PET_UNLOCKED, e);
        }
    }

    public static async shareFieldUnlock(fieldId: string) {
        const base64Image = await PayloadHelper.getFieldUnlockBase64(fieldId);

        try {
            await Game.sdk.share(
                Game.locale.getText('ShareFieldMessage'),
                base64Image,
                {
                    data: PayloadType.SHARE_FIELD_UNLOCKED,
                    customEntryPoint: SocialActionTypes.WALLPOST_SHARE_FIELD_UNLOCKED,
                },
                'REQUEST'
            );
            GameActions.restoreContext(ResponseStatus.SUCCESS, ResponseType.SHARE_FIELD_UNLOCK);

            Game.sdk.gameTracking(FBTrackingEvent.NewField_Share);

            addAndSendSocialActionEvent({
                type: SocialActionTypes.WALLPOST_SHARE_FIELD_UNLOCKED,
                text: 'ShareFieldMessage',
            });
        } catch (e) {
            GameActions.restoreContext(ResponseStatus.FAIL, ResponseType.SHARE_FIELD_UNLOCK, e);
        }
    }

    public static async shareMyPetMiniGameJackpot(data: {}) {
        const base64Image: string = await PayloadHelper.getPetMiniGameJackpotBase64();

        try {
            await Game.sdk.share(
                Game.locale.getText('My_Pet_Mini_Game_Jackpot_Share'),
                base64Image,
                {
                    data: PayloadType.SHARE_PET_JACKPOT,
                    customEntryPoint: SocialActionTypes.WALLPOST_SHARE_PET_JACKPOT,
                },
                'REQUEST'
            );

            GameActions.restoreContext(ResponseStatus.SUCCESS, ResponseType.MY_PET_MINI_GAME_JACKPOT, data);

            Game.sdk.gameTracking(FBTrackingEvent.Pet_Jackpot_Share);
            addAndSendSocialActionEvent({
                type: SocialActionTypes.WALLPOST_SHARE_PET_JACKPOT,
                text: 'SharePetJackpot',
            });
        } catch (e) {
            GameActions.restoreContext(ResponseStatus.FAIL, ResponseType.MY_PET_MINI_GAME_JACKPOT, data);
        }
    }

    public static async shareLuckyWheelRewards(_data: ILuckyWheelShareData, _isPremium: boolean) {
        const base64Image: string = await PayloadHelper.getLuckyWheelBase64(_data);
        const socialActionType = _isPremium ? SocialActionTypes.WALLPOST_SHARE_PREMIUM_WHEEL_REWARD : SocialActionTypes.WALLPOST_SHARE_LUCKY_WHEEL_REWARD;
        const responseType = _isPremium ? ResponseType.PREMIUM_WHEEL_REWARD : ResponseType.LUCKY_WHEEL_REWARD;
        const payloadType = _isPremium ? PayloadType.SHARE_PREMIUM_SHEEL_REWARD : PayloadType.SHARE_LUCKY_WHEEL_REWARD;

        try {
            await Game.sdk.share(
                Game.locale.getText(`ShareLuckyWheelReward_${_data.type}`),
                base64Image,
                {
                    data: payloadType,
                    customEntryPoint: socialActionType,
                },
                'REQUEST'
            );

            GameActions.restoreContext(
                ResponseStatus.SUCCESS,
                responseType,
            );

            addAndSendSocialActionEvent({
                type: socialActionType,
                text: _isPremium ? 'SharePremiumWheelReward' : 'ShareLuckyWheelReward',
            });
        } catch (e) {
            console.warn(e);

            GameActions.restoreContext(
                ResponseStatus.FAIL,
                ResponseType.LUCKY_WHEEL_REWARD,
            );
        }
    }

    public static async loadLevelLeaderboard(levelNumber: number) {
        try {
            const entries: IScoreEntry[] = await Game.sdk.getFriendScores(
                Leaderboard.LEVEL_SCORE,
                levelNumber.toString()
            );
            LevelActions.setLevelLeaderboard(entries);
        } catch (e) {
            // Ignore.
            console.info(e);
        }
    }

    public static async displayAdvertisement(
        placement: IAdvertisementPlacement,
        state: IGameState,
        payload?: IAdvertisementPayload
    ) {
        try {
            const adID: string = Random.ID();
            track('adID', adID);
            const result = await Game.sdk.displayAdvertisement(state, placement, payload);

            if (result) {
                GameActions.restoreContext(ResponseStatus.SUCCESS, ResponseType.DISPLAY_ADVERTISEMENT, {
                    ...payload,
                    placement,
                });
            }else {
                GameActions.restoreContext(ResponseStatus.FAIL, ResponseType.DISPLAY_ADVERTISEMENT, {
                    ...payload,
                    placement,
                });
            }

            track('adID', adID);
           
        } catch (e) {
            console.warn(e);

            track('adError', e.message ? e.message.toString() : JSON.stringify(e));
            track('adSuccess', 0);
            GameActions.restoreContext(ResponseStatus.FAIL, ResponseType.DISPLAY_ADVERTISEMENT, {
                ...payload,
                error: e,
                placement,
            });
        }
    }

    public static async subscribeBotOptIn() {
        if (await Game.sdk.subscribeBot()) {
            Game.sdk.gameTracking(FBTrackingEvent.Bot_confirm);
            GameActions.restoreContext(ResponseStatus.SUCCESS, ResponseType.SUBSCRIBE_BOT_OPT_IN);
        } else {
            Game.sdk.gameTracking(FBTrackingEvent.Bot_deny);
            GameActions.restoreContext(ResponseStatus.FAIL, ResponseType.SUBSCRIBE_BOT_OPT_IN);
        }
    }
    public static connectContext(friendId: string): Promise<any> {
        return Game.sdk.createContext(friendId).catch(e => {
            // Ignore "SAME_CONTEXT" error.
            if (e && e.code !== 'SAME_CONTEXT') {
                if (e.code !== 'USER_INPUT') console.error(e);

                throw e;
            }
        });
    }

    public static restoreContext(status: ResponseStatus, type: ResponseType, params?: any) {
        GameActions.restoreContext(status, type, params);
    }

    public static gameTracking(
        event: FBTrackingEvent,
        value?: number | string,
        params?: { [key: string]: string | number }
    ): void{
        Game.sdk.gameTracking(event, value, params)
    }
}
